import type { Chip } from "./chip";
import type { Delta } from "./delta";
import type { InvokeName, Route } from "./route";
import {
  createTrustCatalog,
  deckosTrustDefinitions,
  validateTrustAssignment,
} from "../trust/trust";

export type TransientSurface =
  | { type: "NONE" }
  | { type: "CHIP"; chipId: string; expanded: boolean }
  | { type: "INVOKE"; chipId: string; selectedInvoke?: InvokeName }
  | { type: "CONTEXT_PREVIEW"; chipId: string; invoke: InvokeName }
  | { type: "DELTA_REVIEW"; delta: Delta }
  | { type: "CONFLICT"; delta: Delta };

export type ConflictRecord = {
  id: string;
  delta: Delta;
  currentRevision: number;
};

export function legalProcedures(chip: Chip): Route[] {
  return chip.routes.filter(
    (route) => route.invoke && route.invoke !== "ARCOPEDIA",
  );
}

export function buildContextPacket(chip: Chip, invoke: InvokeName) {
  const packet = {
    host: "CODEX",
    procedure: invoke,
    chip: { id: chip.id, revision: chip.revision, goal: chip.goal ?? null },
    known: chip.known,
    open: chip.open,
    imprints: chip.imprints,
    truth: chip.truth,
    risk: chip.risk,
  };
  const bytes = new TextEncoder().encode(JSON.stringify(packet)).byteLength;
  return { packet, bytes, dependenciesLoaded: 3, dependenciesOmitted: 11 };
}

export function canApplyDelta(chip: Chip, delta: Delta): boolean {
  return (
    delta.chipId === chip.id &&
    delta.baseRevision === chip.revision &&
    delta.nextRevision === delta.baseRevision + 1
  );
}

export function applyDelta(chip: Chip, delta: Delta): Chip | undefined {
  if (!canApplyDelta(chip, delta)) return undefined;
  if (delta.trust) {
    const catalog = createTrustCatalog(deckosTrustDefinitions);
    for (const assignment of delta.trust) {
      const validation = validateTrustAssignment(assignment, catalog);
      if (!validation.ok) return undefined;
    }
  }
  const next = {
    ...chip,
    revision: delta.nextRevision,
    updatedAt: new Date().toISOString(),
  };
  for (const change of delta.changes) {
    if (change.field === "goal") next.goal = String(change.after ?? "");
    if (change.field === "summary") next.summary = String(change.after ?? "");
  }
  if (delta.routes) next.routes = delta.routes;
  if (delta.trust)
    next.trust = delta.trust.map((assignment) => ({ ...assignment }));
  next.truth = delta.truth;
  next.risk = delta.risk;
  return next;
}

export function makeDemoDelta(chip: Chip, invoke: InvokeName): Delta {
  return {
    chipId: chip.id,
    baseRevision: chip.revision,
    nextRevision: chip.revision + 1,
    status: "READY FOR REVIEW",
    changes: [
      {
        field: "goal",
        before: chip.goal,
        after: `Resolve ${invoke.toLowerCase()} procedure output.`,
      },
    ],
    routes: chip.routes,
    truth: "IMPLEMENTS",
    risk: "CLEAR",
    notes: ["Generated locally for the beta invoke loop."],
  };
}
